<script setup lang="ts">
import { computed } from 'vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import { isRecord, toLocalizedNullableNumber, toNullableString } from '@/utils/coerce';
import { formatDate, formatNumber } from '@/utils/formatters';
import type { KycCategory } from '@/types/kyc';

interface AgePersonRecord {
  birthDate: string | null;
  personAgeYears: number | null;
}

interface Props {
  category: KycCategory;
}

const props = defineProps<Props>();

const ageDetails = computed<AgePersonRecord | null>(() => {
  const raw = props.category.data_json;
  if (!isRecord(raw)) return null;

  return {
    birthDate: toNullableString(raw.birth_date),
    personAgeYears: toLocalizedNullableNumber(raw.person_age_years),
  };
});

const formattedAge = computed(() => {
  const years = ageDetails.value?.personAgeYears;
  if (years === null || years === undefined) return 'Idade não informada';

  const rounded = Number(years.toFixed(1));
  const label = formatNumber(rounded);
  return rounded === 1 ? '1 ano' : `${label} anos`;
});
</script>

<template>
  <div class="mt-3">
    <div
      v-if="ageDetails"
      class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
    >
      <div class="grid grid-cols-1 divide-y divide-slate-100 md:grid-cols-[1.2fr_1fr] md:divide-x md:divide-y-0">
        <div class="p-4">
          <div class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Idade da pessoa
          </div>
          <div class="mt-3 flex items-center gap-3">
            <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
              <BaseIcon class="text-2xl">elderly</BaseIcon>
            </div>
            <div>
              <div class="text-2xl font-semibold text-slate-900">{{ formattedAge }}</div>
              <div class="text-sm text-slate-500">Calculada a partir da data de nascimento encontrada</div>
            </div>
          </div>
        </div>

        <div class="p-4">
          <div class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Data de nascimento
          </div>
          <div class="mt-3 flex items-center gap-3">
            <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
              <BaseIcon class="text-2xl">cake</BaseIcon>
            </div>
            <div>
              <div class="text-lg font-semibold text-slate-900">
                {{ formatDate(ageDetails.birthDate, undefined, 'Data não informada') }}
              </div>
              <div class="text-sm text-slate-500">Primeira data de nascimento disponível no relatório</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="text-sm italic text-slate-500">
      Nenhum dado de idade da pessoa encontrado.
    </div>
  </div>
</template>
